import { zodResolver } from "@hookform/resolvers/zod"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { Save } from "lucide-react"
import { DateTime } from "luxon"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import { z } from "zod"

import { getLocations } from "@/api/getLocations.ts"
import { getTypes } from "@/api/getTypes.ts"
import DateTimePicker from "@/components/DateTimePicker.tsx"
import EntitySelector from "@/components/EntitySelector.tsx"
import { Button } from "@/components/ui/button.tsx"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form.tsx"
import { Input } from "@/components/ui/input.tsx"
import type { IdentifiedKPIValue, KPIDefinition, KPIType } from "@/types/kpiTypes.ts"
import type { Location } from "@/types/locationTypes.ts"

import { getAllKPIs } from "../api/getAllKPIs.ts"
import { upsertKPIValue } from "../api/upsertKPIValue.ts"

import type { KPIValueUpdateRequest } from "../types/kpiEditorTypes.ts"

const formSchema = z.object({
  kpiId: z.int().min(1),
  date: z.date(),
  locationId: z.int().min(1),
  typeId: z.int().min(1),
  value: z.number(),
})

export default function KPIValueEditor({
  id,
  value,
  onSave,
}: {
  id?: number
  value?: IdentifiedKPIValue
  onSave?: () => void
}) {
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: value
      ? {
          kpiId: value.kpiId,
          date: DateTime.fromISO(value.date).toJSDate(),
          locationId: value.locationId,
          typeId: value.typeId,
          value: value.value,
        }
      : undefined,
  })

  const queryClient = useQueryClient()

  const mutation = useMutation({
    mutationFn: (data: KPIValueUpdateRequest) => upsertKPIValue(data, id),
    onSuccess: () => {
      toast("KPI Updated")
      queryClient.invalidateQueries({ queryKey: ["kpiValues"] })
    },
  })

  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    if (onSave) onSave()
    const req = {
      ...data,
      date: DateTime.fromJSDate(data.date).toISODate() || "",
    }
    mutation.mutate(req)
  }

  return (
    <Form {...form}>
      <form className="flex flex-col gap-3" onSubmit={form.handleSubmit(handleSubmit)}>
        <FormField
          control={form.control}
          name="kpiId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>KPI</FormLabel>
              <FormControl>
                <EntitySelector<KPIDefinition>
                  value={field.value}
                  setValue={field.onChange}
                  displayProp="name"
                  fetcher={getAllKPIs}
                  queryKey="kpis"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="date"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Date</FormLabel>
              <FormControl>
                <DateTimePicker value={field.value} onChange={field.onChange} dateOnly={true} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="locationId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Location</FormLabel>
              <FormControl>
                <EntitySelector<Location>
                  value={field.value}
                  setValue={field.onChange}
                  displayProp="name"
                  fetcher={getLocations}
                  queryKey="locations"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="typeId"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Type</FormLabel>
              <FormControl>
                <EntitySelector<KPIType>
                  value={field.value}
                  setValue={field.onChange}
                  displayProp="name"
                  fetcher={getTypes}
                  queryKey="types"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="value"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Value</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  value={field.value}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  className="bg-white"
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={mutation.isPending}>
            <Save /> Save
          </Button>
        </div>
      </form>
    </Form>
  )
}
